// Presentation pieces shared by the list and the detail view. Kept together so
// a phase reads the same colour in the table, the stats strip and the timeline
// — a monitor is only useful if one glance means one thing everywhere.

/**
 * Pipeline phases, in the order a package moves through them. `external` sits
 * at the end because it is not a stage: it marks a package that was pushed
 * without ever being ingested by the pipeline.
 */
/**
 * Mirrors `config/newfiSplitEdocPhases.js` on the backend, in the order a
 * package moves through them.
 *
 * The four write-back phases are separate here for the same reason they are
 * separate there: each is one irreversible write into a lender's loan file, and
 * a package stuck in `obsoleting` is a materially different problem from one
 * stuck in `uploading`. Collapsing them for display would hide exactly the
 * distinction the redesign was built to expose.
 *
 * `not_actionable` is terminal and benign: the pipeline reached the package and
 * correctly found nothing to do — MeridianLink had already obsoleted it, it was
 * deleted off the loan, its type is not a package, or it is one of our own
 * splits coming back round through the modified feed. It reads neutral, not
 * red, because nobody needs to act on it. Until 8 Sep 2026 these landed in
 * `failed` and buried the real faults.
 *
 * `reassociating` comes after the push itself: the splits are filed, the
 * original is obsolete and the loan is stamped, and a condition link MeridianLink
 * did not take is being retried, every 5 minutes for up to an hour.
 *
 * `indexed_only` is terminal and a success: a configured client that does not
 * push to MeridianLink had its package split, classified and planned in Addy,
 * and deliberately nothing was written to the lender's file.
 *
 * `import`, `abandoned` and `external` are legacy — records created before the
 * state machine. Kept so historical packages still render with a name.
 */
export const PHASE_ORDER = [
  "detected",
  "processing",
  "ready",
  "uploading",
  "associating",
  "obsoleting",
  "stamping",
  "reassociating",
  "pushed",
  "out_of_scope",
  "not_actionable",
  "indexed_only",
  "blocked",
  "failed",
  "import",
  "abandoned",
  "external",
];

export const PHASE_LABELS = {
  detected: "Detected",
  processing: "Processing",
  ready: "Ready to push",
  uploading: "Uploading",
  associating: "Associating conditions",
  obsoleting: "Obsoleting original",
  stamping: "Stamping loan",
  reassociating: "Retrying condition links",
  pushed: "Pushed",
  out_of_scope: "Out of scope",
  not_actionable: "Not actionable",
  indexed_only: "Indexed, not pushed",
  blocked: "Blocked",
  failed: "Failed",
  // Legacy.
  import: "Importing",
  abandoned: "Abandoned",
  external: "External",
};

/** Phases that mean a person has to look at the package. */
export const PHASES_NEEDING_ATTENTION = ["blocked", "failed"];

export const TRACE_PHASE_LABELS = {
  detect: "Detect",
  import: "Import",
  processing: "Processing",
  writeback: "Write-back",
};

export const formatPhase = (phase) =>
  PHASE_LABELS[phase] || (phase || "unknown").replace(/_/g, " ");

export const PhasePill = ({ phase }) => (
  <span className={`nse-pill nse-pill--${phase || "unknown"}`}>
    {formatPhase(phase)}
  </span>
);

export const LevelDot = ({ level }) => (
  <span className={`nse-dot nse-dot--${level || "info"}`} title={level} />
);

/** Absolute clock time — a timeline needs the real time, not "3h ago". */
export const formatClock = (ms) => {
  if (!ms) {
    return "—";
  }
  try {
    return new Date(ms).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "—";
  }
};

export const formatDateTime = (ms) => {
  if (!ms) {
    return "—";
  }
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
};

export const formatRelative = (ms) => {
  if (!ms) {
    return "—";
  }
  const diff = Date.now() - ms;
  if (diff < 0) {
    return "just now";
  }
  const sec = Math.floor(diff / 1000);
  if (sec < 60) {
    return "just now";
  }
  const min = Math.floor(sec / 60);
  if (min < 60) {
    return `${min}m ago`;
  }
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    return `${hr}h ago`;
  }
  const day = Math.floor(hr / 24);
  if (day < 30) {
    return `${day}d ago`;
  }
  return formatDateTime(ms);
};

/** Long ids are unreadable in a table but must stay copyable in full. */
export const Mono = ({ value, truncate = 0 }) => {
  if (!value) {
    return <span className="nse-muted">—</span>;
  }
  const text = String(value);
  const shown =
    truncate > 0 && text.length > truncate ? `${text.slice(0, truncate)}…` : text;
  return (
    <span className="nse-mono" title={text}>
      {shown}
    </span>
  );
};

/**
 * The banner that appears when a request ran out of scan budget before it
 * filled its page.
 *
 * Worth its own component rather than a muted footnote: without it a short page
 * is indistinguishable from the end of the results, which is exactly the wrong
 * conclusion to let someone draw while they are hunting a failure.
 */
export const ScanExhaustedNotice = ({ scanned, onLoadMore }) => (
  <div className="nse-notice nse-notice--warn">
    <strong>Partial page.</strong> Scanned {(scanned || 0).toLocaleString()}{" "}
    records without filling it — the filters matched very few. Keep loading to
    continue from where this stopped.
    {onLoadMore && (
      <button type="button" className="nse-btn nse-btn--small" onClick={onLoadMore}>
        Load more
      </button>
    )}
  </div>
);

/**
 * A missing Firestore composite index is the one failure this page will
 * genuinely hit — the backend returns FIRESTORE_INDEX_MISSING for it — and it
 * is not a bug to report, it is a deploy that has not happened. Saying so beats
 * a red box full of stack.
 */
const isMissingIndexError = (error) =>
  typeof error === "string" && error.includes("FIRESTORE_INDEX_MISSING");

/** Empty, loading and error states, so no view invents its own. */
export const StateBlock = ({ loading, error, empty, emptyText, children }) => {
  if (error && isMissingIndexError(error)) {
    return (
      <div className="nse-notice nse-notice--warn">
        <strong>Waiting on a Firestore index.</strong> This view needs a
        composite index that has not been deployed to this environment yet. It
        ships with <code>firestore.indexes.json</code>; CI creates it on the
        next deploy. Package detail and the filter lists work without it.
      </div>
    );
  }
  if (error) {
    return (
      <div className="nse-notice nse-notice--error">
        <strong>Could not load.</strong>
        <pre className="nse-error-detail">{error}</pre>
      </div>
    );
  }
  if (loading) {
    return <div className="nse-state">Loading…</div>;
  }
  if (empty) {
    return <div className="nse-state nse-muted">{emptyText}</div>;
  }
  return children;
};
